const DEFAULT_LEVEL_BAND_SIZE = 20;

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clampCoefficient(value) {
  return Math.min(1.45, Math.max(0.65, numberValue(value, 1) || 1));
}

export function settlementLevelBand(level, bandSize = DEFAULT_LEVEL_BAND_SIZE) {
  const normalizedLevel = Math.trunc(numberValue(level, 0));
  const normalizedBandSize = Math.max(1, Math.trunc(numberValue(bandSize, DEFAULT_LEVEL_BAND_SIZE)));
  if (normalizedLevel < 1) return null;
  const levelFrom = Math.floor((normalizedLevel - 1) / normalizedBandSize) * normalizedBandSize + 1;
  return {
    levelFrom,
    levelTo: levelFrom + normalizedBandSize - 1,
    key: `${levelFrom}:${levelFrom + normalizedBandSize - 1}`,
  };
}

function normalizeBossStat(row = {}) {
  const bossNo = Math.trunc(numberValue(row.bossNo ?? row.boss_no, 0));
  if (bossNo < 1 || bossNo > 5) return null;
  return {
    bossNo,
    attackCount: Math.max(0, Math.trunc(numberValue(row.attackCount ?? row.attack_count, 0))),
    unionCount: Math.max(0, Math.trunc(numberValue(row.unionCount ?? row.union_count, 0))),
    coefficient: clampCoefficient(row.coefficient),
    usedAllLevelFallback: Boolean(row.usedAllLevelFallback ?? row.used_all_level_fallback),
  };
}

export function normalizeSettlementCorrectionStats(raw = null) {
  if (!raw || typeof raw !== 'object') return null;
  const levelBandSize = Math.max(1, Math.trunc(numberValue(raw.levelBandSize ?? raw.level_band_size, DEFAULT_LEVEL_BAND_SIZE)));
  const allLevelByBoss = new Map();
  for (const row of Array.isArray(raw.allLevels ?? raw.all_levels) ? (raw.allLevels ?? raw.all_levels) : []) {
    const normalized = normalizeBossStat(row);
    if (normalized) allLevelByBoss.set(normalized.bossNo, normalized);
  }

  const bandByKey = new Map();
  for (const bandRow of Array.isArray(raw.bands) ? raw.bands : []) {
    const levelFrom = Math.trunc(numberValue(bandRow.levelFrom ?? bandRow.level_from, 0));
    const levelTo = Math.trunc(numberValue(bandRow.levelTo ?? bandRow.level_to, levelFrom + levelBandSize - 1));
    if (levelFrom < 1 || levelTo < levelFrom) continue;
    const bosses = new Map();
    for (const row of Array.isArray(bandRow.bosses) ? bandRow.bosses : []) {
      const normalized = normalizeBossStat(row);
      if (normalized) bosses.set(normalized.bossNo, normalized);
    }
    bandByKey.set(`${levelFrom}:${levelTo}`, {
      levelFrom,
      levelTo,
      attackCount: Math.max(0, Math.trunc(numberValue(bandRow.attackCount ?? bandRow.attack_count, 0))),
      unionCount: Math.max(0, Math.trunc(numberValue(bandRow.unionCount ?? bandRow.union_count, 0))),
      bosses,
    });
  }

  return {
    seasonNo: Math.trunc(numberValue(raw.seasonNo ?? raw.season_no, 0)),
    levelBandSize,
    unionCount: Math.max(0, Math.trunc(numberValue(raw.unionCount ?? raw.union_count, 0))),
    attackCount: Math.max(0, Math.trunc(numberValue(raw.attackCount ?? raw.attack_count, 0))),
    allLevelByBoss,
    bandByKey,
  };
}

export function settlementCorrectionForRecord(stats, record = {}, fallbackCoefficient = 1) {
  const normalizedStats = stats?.allLevelByBoss instanceof Map ? stats : normalizeSettlementCorrectionStats(stats);
  const bossNo = Math.trunc(numberValue(record.bossNo ?? record.boss_no, 0));
  const fallback = clampCoefficient(fallbackCoefficient);
  if (!normalizedStats || bossNo < 1 || bossNo > 5) return {
    coefficient: fallback,
    source: 'local_fallback',
    levelBand: null,
    attackCount: 0,
    unionCount: 0,
  };

  const levelBand = settlementLevelBand(record.charLevel ?? record.char_level, normalizedStats.levelBandSize);
  const bandStat = levelBand ? normalizedStats.bandByKey.get(levelBand.key)?.bosses?.get(bossNo) : null;
  if (bandStat && !bandStat.usedAllLevelFallback) {
    return {
      coefficient: bandStat.coefficient,
      source: 'level_band',
      levelBand,
      attackCount: bandStat.attackCount,
      unionCount: bandStat.unionCount,
    };
  }
  if (bandStat) {
    return {
      coefficient: bandStat.coefficient,
      source: 'all_level_fallback',
      levelBand,
      attackCount: bandStat.attackCount,
      unionCount: bandStat.unionCount,
    };
  }

  const allLevelStat = normalizedStats.allLevelByBoss.get(bossNo);
  if (allLevelStat) {
    return {
      coefficient: allLevelStat.coefficient,
      source: 'all_level_fallback',
      levelBand,
      attackCount: allLevelStat.attackCount,
      unionCount: allLevelStat.unionCount,
    };
  }

  return {
    coefficient: fallback,
    source: 'local_fallback',
    levelBand,
    attackCount: 0,
    unionCount: 0,
  };
}

export { DEFAULT_LEVEL_BAND_SIZE };
